import {
  getDateTimeFormat,
  getTranslatedText,
  withErrorBoundary,
  useIntl,
  getWeekOfDate
} from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import {
  Button,
  TextStyle,
  Popover,
  DateTimePicker
} from '@superops/ui-design-system';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const WeekPickerStyles = styled.div`
  padding: 15px;
  .place-holders {
    width: 165px;
    background: #eaeaea4d;
    border-radius: 5px;
    cursor: pointer;
    margin-right: 10px;
    margin-bottom: 10px;
    padding: 10px 10px 10px 15px;
    &.active {
      border: 1px solid #000;
    }
  }
  .react-datepicker {
    box-shadow: none;
  }

  .react-datepicker__day--today {
    color: #000;
    background: unset;
  }

  .react-datepicker__day--selected {
    background: var(--sops-day-picker-selected-date) !important;
    color: #fff !important;
  }
`;

const PLACE_HOLDERS = [
  {
    label: 'placeholder.this.week',
    value: 'this.week'
  },
  {
    label: 'placeholder.last.week',
    value: 'last.week'
  }
];

const WeekPicker = (props) => {
  const [selectedDate, setSelectedDate] = useState(props?.selected || null); // date selected on date picker
  const [displayRange, setDisplayRange] = useState(null); // we'll pass date range and the picker will get the display format;
  const [popoverActive, setPopoverActive] = useState(false);
  const placeHoldersToRemove = props?.placeHoldersToRemove || [];
  const availablePlaceHolders = PLACE_HOLDERS.filter(
    (el) => !placeHoldersToRemove.includes(el?.value)
  );

  const getSelectedDateForDatePicker = () => {
    if (selectedDate && new Date(selectedDate) !== 'Invalid Date') {
      return new Date(selectedDate);
    } else {
      return null;
    }
  };
  const selectedDateForWeekPicker = getSelectedDateForDatePicker();

  const intl = useIntl();

  const togglePopOver = () => {
    setPopoverActive(!popoverActive);
  };

  const dateFormat = getDateTimeFormat().dateFormat;

  useEffect(() => {
    if (props?.selected) {
      const formatSelected = dayjs(props?.selected).format('YYYY-MM-DD');
      const selectedWeek = getWeekOfDate(formatSelected);
      const currentWeek = selectedDate ? getWeekOfDate(new Date()) : null;

      const lastWeek = getWeekOfDate(dayjs(new Date()).subtract(1, 'week'));

      const start = dayjs(selectedWeek?.[0]).toString();
      const end = dayjs(selectedWeek?.[6]).toString();

      const isInThisWeek = currentWeek.find((el) => el === formatSelected);
      const isLastWeek = lastWeek.find((el) => el === formatSelected);
      let displayValue = `${dayjs(start).format('YYYY-MM-DD')} -
        ${dayjs(end).format('YYYY-MM-DD')}`;

      if (isInThisWeek) {
        displayValue = getTranslatedText(intl, PLACE_HOLDERS[0].label);
      }
      if (isLastWeek) {
        displayValue = getTranslatedText(intl, PLACE_HOLDERS[1].label);
      }
      setDisplayRange(displayValue);
      setSelectedDate(props?.selected);
    }
  }, [props?.selected]);

  const toggleNextWeek = () => {
    const currentDay = dayjs(selectedDate || new Date());
    const nextWeekDay = currentDay.add(7, 'day').toString();
    setSelectedDate(nextWeekDay);

    const nextWeek = getWeekOfDate(dayjs(nextWeekDay).format('YYYY-MM-DD'));

    const start = dayjs(nextWeek?.[0]);
    const end = dayjs(nextWeek?.[6]);

    const displayValue = `${dayjs(start).format('YYYY-MM-DD')} - ${dayjs(
      end
    ).format('YYYY-MM-DD')}`;
    setDisplayRange(displayValue);

    if (props?.onChange) {
      props.onChange({ selectedDate: nextWeekDay, weekRange: nextWeek });
    }
  };

  const togglePrevWeek = () => {
    const currentDay = dayjs(selectedDate || new Date());
    const prevWeekDay = currentDay.subtract(7, 'day').toString();
    setSelectedDate(prevWeekDay);

    const prevWeek = getWeekOfDate(dayjs(prevWeekDay).format('YYYY-MM-DD'));

    const start = dayjs(prevWeek?.[0]);
    const end = dayjs(prevWeek?.[6]);

    const displayValue = `${dayjs(start).format('YYYY-MM-DD')} - ${dayjs(
      end
    ).format('YYYY-MM-DD')}`;
    setDisplayRange(displayValue);

    if (props?.onChange) {
      props.onChange({ selectedDate: prevWeekDay, weekRange: prevWeek });
    }
  };

  const currentWeek = selectedDate
    ? getWeekOfDate(dayjs(selectedDate).format('YYYY-MM-DD'))
    : null;

  const toggleThisOrLastWeek = (data) => {
    const { label, value } = data;

    let day = dayjs(new Date());

    let week = getWeekOfDate(dayjs(day).format('YYYY-MM-DD'));

    if (value === 'last.week') {
      day = day.subtract(7, 'day').toString();
      week = getWeekOfDate(dayjs(day).format('YYYY-MM-DD'));
    }

    const displayVal = getTranslatedText(intl, label);
    setDisplayRange(displayVal);
    setSelectedDate(day);
    if (props?.onChange) {
      props.onChange({ selectedDate: day, weekRange: week });
    }
  };

  return (
    <div className="flex  g20">
      <div>
        <Button
          onClick={togglePrevWeek}
          icon={<SVGIcon name="ArrowLeft" size="sm" />}
          disabled={props?.previousWeekDisabled}
        />
      </div>

      <div className="flex actr g10">
        <div>
          <Popover
            activator={
              <div className="flex g5 actr pointer" onClick={togglePopOver}>
                <div className="mt3 fx">
                  <SVGIcon name="DateTime" />
                </div>
                <div className="f-14">
                  <TextStyle
                    variation={!displayRange && 'subdued'}
                    weight="SemiBold"
                  >
                    <div>{displayRange || 'Select range'} </div>
                  </TextStyle>
                </div>
              </div>
            }
            active={popoverActive}
            onClose={() => {}}
            fullHeight
            zIndexOverride={520}
          >
            <WeekPickerStyles>
              <div className="flex main-wrapper">
                <div className="place-holder-wrapper">
                  {availablePlaceHolders.map((el) => {
                    const displayValue = getTranslatedText(intl, el?.label);
                    const isActive = displayValue === displayRange;
                    return (
                      <div
                        className={`place-holders ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          togglePopOver();
                          toggleThisOrLastWeek(el);
                        }}
                      >
                        {displayValue}
                      </div>
                    );
                  })}
                </div>
                <div className="date-picker-wrapper">
                  <DateTimePicker
                    inline
                    popperPlacement="bottom"
                    shouldCloseOnSelect
                    dateFormat={dateFormat}
                    locale="enGb"
                    selectedDate={selectedDate}
                    selected={selectedDateForWeekPicker}
                    minDate={props?.minDate || null}
                    dayClassName={(d) => {
                      const isInSameWeek = currentWeek
                        ? currentWeek?.includes(dayjs(d).format('YYYY-MM-DD'))
                        : false;
                      return isInSameWeek
                        ? 'react-datepicker__day--selected'
                        : '';
                    }}
                    onChange={(date) => {
                      const weekRange = getWeekOfDate(
                        dayjs(date).format('YYYY-MM-DD')
                      );
                      const firstDate = weekRange[0];
                      const firstDateFormat = dayjs(firstDate).format(
                        'YYYY-MM-DD'
                      );

                      const lastdate = weekRange[6];
                      const lastDateFormat = dayjs(lastdate).format(
                        'YYYY-MM-DD'
                      );
                      setSelectedDate(date);

                      const displayVal = `${firstDateFormat} - ${lastDateFormat}`;
                      setDisplayRange(displayVal);
                      togglePopOver();
                      if (props?.onChange) {
                        props.onChange({ selectedDate: date, weekRange });
                      }
                    }}
                    portalId="new-portal-3"
                  />
                </div>
              </div>
            </WeekPickerStyles>
          </Popover>
        </div>
      </div>

      <div>
        <Button onClick={toggleNextWeek} icon={<SVGIcon name="ArrowRight" />} />
      </div>
    </div>
  );
};

export default withErrorBoundary(WeekPicker);
